import { addYears, subYears } from "date-fns"

import { type IVariantSummary } from "~/hooks/useVariantTableData"
import {
  DateFormatType,
  formatDate,
  getSoldAndActiveSince,
  startOfTodayUTC,
} from "~/utils/datetime"
import { formatName } from "~/utils/namingUtils"

const ALERT_UNEXPLAINED = "Model trend"
const ALERT_STOCKOUT = "Stockout"
const ALERT_MISSING_SALES = "Estimated un-observed demand"
const ALERT_PHASING_OUT = "Phase-out"
export const ALERT_NO_ALERT = "No alert"

export type ForecastBreakdown = {
  sales: number
  forecast: number
  futurePreOrders: number
  lastYearPreOrders: number
  preOrderChange: number
  totalUnknownPreOrders: number
  stockoutTotal: number
  stockoutWeeks: number
  missingSalesTotal: number
  missingSalesWeeks: number
  observationWeeks: number
  manualAdjustment: number
  fashionForecast: number
  excludedFromForecast: number
  phasingOutEffect: number
  phasingOutWeeks: number
  unexplained: number
  totalGrowth: number
}

export type NpiForecastBreakdown = {
  forecast: number
  knownPreorders: number
  manualAdjustment: number
  fashionForecast: number
  rawModelForecast: number | null
}

const aggregatesOf = (entries: IVariantSummary[]) =>
  entries.map((e) => e.forecastAlertAggregate).filter((a) => a != null)

// Phased-out / phasing-out variant with no breakdown aggregate row at all — the forecast
// model generated nothing for it, so its entire demand drop is attributable to phase-out.
export function isNoDemandPhaseOut(entry: IVariantSummary): boolean {
  const status = entry.settings?.lifecycleStatus
  return (status === "phasedOut" || status === "phasingOut") && entry.forecastAlertAggregate == null
}

export function getNpiForecastBreakdown(entries: IVariantSummary[]): NpiForecastBreakdown {
  const forecast = entries.reduce((sum, e) => sum + (e.forecastQuantity ?? 0), 0)
  const aggs = aggregatesOf(entries)
  const knownPreorders = aggs.reduce((sum, a) => sum + (a.futurePreOrders ?? 0), 0)
  const manualAdjustment = aggs.reduce((sum, a) => sum + (a.manualAdjustment ?? 0), 0)
  const fashionForecast = aggs.reduce((sum, a) => sum + (a.fashionForecast ?? 0), 0)
  const hasRawForecast = aggs.some((a) => a.rawForecast != null)
  const rawModelForecast = hasRawForecast
    ? aggs.reduce((sum, a) => sum + (a.rawForecast ?? 0), 0)
    : null

  return {
    forecast,
    knownPreorders,
    manualAdjustment,
    fashionForecast,
    rawModelForecast,
  }
}

export function getForecastBreakdown(entries: IVariantSummary[]): ForecastBreakdown {
  const sales = entries.reduce((sum, e) => sum + (e.salesQuantity ?? 0), 0)
  const forecast = entries.reduce((sum, e) => sum + (e.forecastQuantity ?? 0), 0)
  const lastYearPreOrders = entries.reduce((sum, e) => sum + (e.lastYearPreOrders ?? 0), 0)
  const aggs = aggregatesOf(entries)
  const futurePreOrders = aggs.reduce((sum, a) => sum + (a.futurePreOrders ?? 0), 0)
  const preOrderChange = futurePreOrders - lastYearPreOrders
  const totalUnknownPreOrders = aggs.reduce((sum, a) => sum + (a.totalUnknownPreOrders ?? 0), 0)
  const stockoutTotal = aggs.reduce((sum, a) => sum + (a.stockoutTotal ?? 0), 0)
  const stockoutWeeks = aggs.reduce((sum, a) => sum + (a.stockoutWeeks ?? 0), 0)
  const missingSalesTotal = aggs.reduce((sum, a) => sum + (a.missingSalesTotal ?? 0), 0)
  const missingSalesWeeks = aggs.reduce((sum, a) => sum + (a.missingSalesWeeks ?? 0), 0)
  const observationWeeks = aggs[0]?.observationWeeks ?? 52
  const manualAdjustment = aggs.reduce((sum, a) => sum + (a.manualAdjustment ?? 0), 0)
  const fashionForecast = aggs.reduce((sum, a) => sum + (a.fashionForecast ?? 0), 0)
  const excludedFromForecast = aggs.reduce((sum, a) => sum + (a.excludedFromForecast ?? 0), 0)
  // Phased-out variants (and phasing-out ones when sell-down is disabled) get no forecast
  // rows at all, so there is no SQL phasingOutEffect to read — attribute their full demand
  // drop to phase-out instead of leaving it in the unexplained (Model trend) residual.
  const noDemandPhaseOut = entries.reduce(
    (sum, e) =>
      isNoDemandPhaseOut(e) ? sum + (e.forecastQuantity ?? 0) - (e.salesQuantity ?? 0) : sum,
    0,
  )
  const phasingOutEffect =
    aggs.reduce((sum, a) => sum + (a.phasingOutEffect ?? 0), 0) + noDemandPhaseOut
  const phasingOutWeeks = aggs.reduce((max, a) => Math.max(max, a.phasingOutWeeks ?? 0), 0)
  const totalGrowth = forecast - sales
  const unexplained =
    totalGrowth -
    preOrderChange -
    totalUnknownPreOrders -
    stockoutTotal -
    missingSalesTotal -
    manualAdjustment -
    fashionForecast -
    excludedFromForecast -
    phasingOutEffect

  return {
    sales,
    forecast,
    futurePreOrders,
    lastYearPreOrders,
    preOrderChange,
    totalUnknownPreOrders,
    stockoutTotal,
    stockoutWeeks,
    missingSalesTotal,
    missingSalesWeeks,
    observationWeeks,
    manualAdjustment,
    fashionForecast,
    excludedFromForecast,
    phasingOutEffect,
    phasingOutWeeks,
    unexplained,
    totalGrowth,
  }
}

// Every row's info badge compares against these, yet they only move at a UTC day boundary.
let yearBoundsOfDay: { day: number; oneYearAgo: Date; oneYearAhead: Date } | undefined
function yearBounds() {
  const day = Math.floor(Date.now() / 86_400_000)
  if (yearBoundsOfDay?.day !== day) {
    const today = startOfTodayUTC()
    yearBoundsOfDay = { day, oneYearAgo: subYears(today, 1), oneYearAhead: addYears(today, 1) }
  }
  return yearBoundsOfDay
}

type InfoType = {
  id:
    | "fashionCollection"
    | "manuallyMatched"
    | "adjusted"
    | "phasedOut"
    | "phasingOut"
    | "shortSaleHorizonReactivated"
    | "shortSaleHorizon"
    | "shortDemandHorizon"
  label: string
  match: (data: IVariantSummary) => boolean
  tooltip: (data: IVariantSummary) => string
}

const INFO_TYPES: InfoType[] = [
  {
    id: "fashionCollection",
    label: "Fashion collection",
    match: (data) => (data.forecastAlertAggregate?.fashionWeeks ?? 0) > 0,
    tooltip: (data) => {
      const weeks = data.forecastAlertAggregate?.fashionWeeks ?? 0
      return `Demand based on a fashion collection model for \n${weeks} week${weeks !== 1 ? "s" : ""}`
    },
  },
  {
    id: "manuallyMatched",
    label: "Manually matched",
    match: (data) => data.forecastAlertAggregate?.isManualMatch === true,
    tooltip: (data) => {
      const count = data.forecastAlertAggregate?.customMatchCount ?? 0
      return count > 0
        ? `Demand based on ${count} manually selected match${count !== 1 ? "es" : ""}`
        : "Demand based on a manual match"
    },
  },
  {
    id: "adjusted",
    label: "Adjusted",
    match: (data) => (data.forecastAlertAggregate?.adjustedWeeks ?? 0) > 0,
    tooltip: (data) => {
      const adjustedWeeks = data.forecastAlertAggregate?.adjustedWeeks ?? 0
      return `Demand has been manually adjusted for \n${adjustedWeeks} week${adjustedWeeks !== 1 ? "s" : ""}`
    },
  },
  {
    id: "phasedOut",
    label: "Phased out",
    match: (data) => data.settings?.lifecycleStatus === "phasedOut",
    tooltip: () => "Product is phased out\nNo demand is generated",
  },
  {
    id: "phasingOut",
    label: "Phasing out",
    match: (data) => data.settings?.lifecycleStatus === "phasingOut",
    tooltip: (data) => {
      const phasingOutFrom = data.settings?.phasingOutFrom
      const sellDown = (data.forecastAlertAggregate?.phasingOutEffect ?? 0) !== 0
      return [
        "Product is being phased out",
        phasingOutFrom
          ? `Phasing out from: ${formatDate(phasingOutFrom, DateFormatType.MonthYear)}`
          : undefined,
        sellDown
          ? "Demand is limited to selling down remaining stock"
          : "Demand tapers off from the phase-out date",
      ]
        .filter(Boolean)
        .join("\n")
    },
  },
  {
    id: "shortSaleHorizonReactivated",
    label: `Short ${formatName("sale", true)} horizon (reactivated)`,
    match: (data) => {
      const oneYearAgo = yearBounds().oneYearAgo
      const noosStartDate = (data.settings as { noosStartDate?: Date })?.noosStartDate
      // Active history is measured from the ERP NOOS start date, NOT the generic
      // activatedFrom (which can come from collections/preorders/manual overrides).
      // This ensures only NOOS items (e.g. Hummel) are ever flagged "(reactivated)".
      const activeSince = getSoldAndActiveSince(data.soldSince, noosStartDate)
      // Strict: a product active & selling for a full year has 52 weeks and is
      // not a short/reactivated horizon.
      if (activeSince && activeSince <= oneYearAgo) return false
      // Either prior sales resumed after a NOOS (re)activation gap, or the NOOS
      // start date itself falls within the last year.
      return (
        (data.soldSince != null && data.soldSince < oneYearAgo) ||
        (noosStartDate != null && noosStartDate > oneYearAgo)
      )
    },
    tooltip: (data) => {
      const noosStartDate = (data.settings as { noosStartDate?: Date })?.noosStartDate
      const activeSince = getSoldAndActiveSince(data.soldSince, noosStartDate)
      return [
        `Less than 52 weeks of ${formatName("sale", true)} data`,
        activeSince
          ? `Active & selling since: ${formatDate(activeSince, DateFormatType.MonthYear)}`
          : "Active & selling since: unknown",
        data.soldSince
          ? `First ${formatName("sale")}: ${formatDate(data.soldSince, DateFormatType.MonthYear)}`
          : `First ${formatName("sale")}: unknown`,
      ].join("\n")
    },
  },
  {
    id: "shortSaleHorizon",
    label: `Short ${formatName("sale", true)} horizon`,
    match: (data) => {
      const oneYearAgo = yearBounds().oneYearAgo
      const noosStartDate = (data.settings as { noosStartDate?: Date })?.noosStartDate
      // Measure the sales horizon from actual sales (and the ERP NOOS start),
      // NOT the generic activatedFrom — an item selling for >52 weeks is not a
      // short horizon even if it was recently re-activated via a collection.
      const activeSince = getSoldAndActiveSince(data.soldSince, noosStartDate)
      return !activeSince || activeSince > oneYearAgo
    },
    tooltip: (data) =>
      [
        `Less than 52 weeks of ${formatName("sale", true)} data`,
        data.soldSince
          ? `Sold since: ${formatDate(data.soldSince, DateFormatType.MonthYear)}`
          : "Sold since: unknown",
      ].join("\n"),
  },
  {
    id: "shortDemandHorizon",
    label: "Short demand horizon",
    match: (data) => {
      const activatedUntil = (data.settings as { activatedUntil?: Date })?.activatedUntil
      return !!activatedUntil && activatedUntil < yearBounds().oneYearAhead
    },
    tooltip: (data) => {
      const activatedUntil = (data.settings as { activatedUntil?: Date })?.activatedUntil
      return [
        "Product deactivated within 52 weeks",
        activatedUntil
          ? `Deactivates: ${formatDate(activatedUntil, DateFormatType.MonthYear)}`
          : "Deactivation date: unknown",
      ].join("\n")
    },
  },
]

function isForecastAlertEligible(data: IVariantSummary | undefined): data is IVariantSummary {
  if (!data) return false
  if (data.settings?.productType !== "noos") return false
  if (data.isNpi) return false
  if ((data.salesQuantity ?? 0) === 0) return false
  return true
}

const NPI_INFO_TYPES = INFO_TYPES.filter(
  (t) => t.id === "adjusted" || t.id === "manuallyMatched" || t.id === "fashionCollection",
)

export function getInfoMatch(data: IVariantSummary | undefined): InfoType | undefined {
  if (!data) return undefined
  if (data.settings?.productType !== "noos") return undefined
  if (data.isNpi) return NPI_INFO_TYPES.find((t) => t.match(data))
  if (!isForecastAlertEligible(data)) return undefined
  return INFO_TYPES.find((info) => info.match(data))
}

export function getInfoValue(data: IVariantSummary | undefined): string | undefined {
  return getInfoMatch(data)?.label
}

export function getInfoTooltip(data: IVariantSummary | undefined): string | undefined {
  if (!data) return undefined
  const info = getInfoMatch(data)
  return info ? info.tooltip(data) : undefined
}

export type BaselineQuality = {
  stockout?: { units: number; weeks: number }
  unobserved?: { units: number; weeks: number }
}

/**
 * What the trailing year does not measure: weeks the variant was out of stock, and weeks it was active with
 * nothing recorded. Both mean realised sales — which is what a demand-2 baseline *is* — sit below real
 * demand, so they are a caveat on the baseline number rather than a row state.
 *
 * Same eligibility and threshold as {@link getAlerts}, minus its info-state gate: that gate exists only
 * because a one-badge cell has to pick a single label, and a phasing-out variant's history is understated
 * just the same.
 */
export function getBaselineQuality(
  data: IVariantSummary | undefined,
  forecastAlertThreshold: number,
): BaselineQuality | undefined {
  if (!isForecastAlertEligible(data)) return undefined
  const salesLastYear = data.salesQuantity ?? 0
  const bd = getForecastBreakdown([data])
  const quality: BaselineQuality = {}
  if (Math.abs(bd.stockoutTotal / salesLastYear) >= forecastAlertThreshold)
    quality.stockout = { units: bd.stockoutTotal, weeks: bd.stockoutWeeks }
  if (Math.abs(bd.missingSalesTotal / salesLastYear) >= forecastAlertThreshold)
    quality.unobserved = { units: bd.missingSalesTotal, weeks: bd.missingSalesWeeks }
  return quality.stockout || quality.unobserved ? quality : undefined
}

export function getAlerts(
  data: IVariantSummary | undefined,
  forecastAlertThreshold: number,
  precomputedBreakdown?: ForecastBreakdown,
): string[] | undefined {
  if (data?.isNpi && data?.settings?.productType === "noos") return []
  if (!isForecastAlertEligible(data)) return undefined
  if (getInfoValue(data)) return []

  const salesLastYear = data.salesQuantity ?? 0
  const bd = precomputedBreakdown ?? getForecastBreakdown([data])
  const alerts: string[] = []
  if (Math.abs(bd.unexplained / salesLastYear) >= forecastAlertThreshold)
    alerts.push(ALERT_UNEXPLAINED)
  const preOrderTotal = bd.preOrderChange + bd.totalUnknownPreOrders
  if (Math.abs(preOrderTotal / salesLastYear) >= forecastAlertThreshold)
    alerts.push(`${formatName("preorder", true, true)} change`)
  if (Math.abs(bd.stockoutTotal / salesLastYear) >= forecastAlertThreshold)
    alerts.push(ALERT_STOCKOUT)
  if (Math.abs(bd.missingSalesTotal / salesLastYear) >= forecastAlertThreshold)
    alerts.push(ALERT_MISSING_SALES)
  // Fires on rows mixing phasing-out and active variants (sku/variant level); pure
  // phasing-out rows terminate in the "Phasing out" info state above instead.
  if (Math.abs(bd.phasingOutEffect / salesLastYear) >= forecastAlertThreshold)
    alerts.push(ALERT_PHASING_OUT)

  return alerts
}
